import moment from 'moment';

// What changed in a log entry, from → to, for the activity entry (US 9438). The log keeps the old and new
// values of an update under properties.old / properties.attributes; created entries keep the new values flat.

// Kept by the system or references to other rows: not listed as changes
const HIDDEN = [
  'id', 'slug', 'client', 'who_am_i', 'created_at', 'updated_at', 'created_by', 'updated_by', 'deleted_at',
  'completed_at', 'completed_by', 'response_rate', 'is_response_valid', 'comment_count', 'comment_unread_count',
  'sign_off_count', 'days_left', 'project_slug', 'full_title', 'model_type', 'scope_type', 'order', 'position',
  'email_verified_at', 'remember_token', 'password', 'api_token', 'logo'
];

// Fields shown as on/off
const SWITCHES = /^(is_|has_|add_|allow_|include_|email_(?!mode))|(_on|_included|_enabled|_required)$|^(scheduled|rules_followed|active|locked|dpo|question)$/;

// Field labels kept elsewhere in the translations
const LABELS = {
  test_phase: 'tasks.test_phase.label',
  domain: 'profile.client.domain_label',
  ctid: 'profile.client.report_ctid_label',
  report_id: 'profile.client.report_id_label',
  page_name: 'profile.client.report_page_name_label',
  runs_as: 'systems_overview.fields.runs_as',
  location: 'systems_overview.fields.location',
  lifecycle_status: 'systems_overview.fields.lifecycle_status',
  in_use_since: 'system_hero.panel.in_use_since',
  covers: 'systems_overview.fields.covers',
  business_residual_risk: 'risk_tab.log.business_residual_risk',
  privacy_residual_risk: 'risk_tab.log.privacy_residual_risk',
  logo: 'system_logo.label'
};

// Values shown with their own labels
const VALUES = {
  test_phase: value => 'tasks.test_phase.' + (value || 'design_implementation'),
  email_mode: value => 'client_page.notifications.mode.' + value,
  risk_type: value => 'profile.client.' + value,
  authentication: value => 'users_page.sign_in.' + value,
  runs_as: value => 'systems_overview.runs_as.' + value,
  lifecycle_status: value => 'systems_overview.lifecycle.' + value,
  covers: value => 'systems_overview.covers.' + value
};

const LONG_TEXT = 90;

function isEmpty(value) {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length);
}

function plain(value) {
  return String(value).replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li|h\d)>/gi, '\n').replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
}

function isJson(value) {
  return typeof value === 'string' && /^\s*[[{]/.test(value) && /[\]}]\s*$/.test(value);
}

// The name a list item or related item is known by
export function itemName(item) {
  if (item === null || item === undefined) {
    return '';
  }
  if (typeof item !== 'object') {
    return String(item);
  }
  return item.name_translated || item.name || item.full_title || item.title || item.email || item.file_name || (item.id !== undefined ? '#' + item.id : '');
}

function label(vm, key, labels) {
  if (labels && labels[key]) {
    return vm.$t(labels[key]);
  }
  if (vm.$te('activity_log.fields.' + key)) {
    return vm.$t('activity_log.fields.' + key);
  }
  if (LABELS[key]) {
    return vm.$t(LABELS[key]);
  }
  const words = key.replace(/_translated$/, '').replace(/_id$/, '').split('_').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// A value as text, the way the app shows it elsewhere
export function formatValue(vm, key, value, formatters) {
  // D&I testing is stored as an empty testing phase
  if (isEmpty(value) && key !== 'test_phase') {
    return null;
  }
  if (formatters && formatters[key]) {
    return formatters[key](value);
  }
  if (VALUES[key]) {
    const path = VALUES[key](value);
    return vm.$te(path) ? vm.$t(path) : String(value);
  }
  if (typeof value === 'boolean' || (SWITCHES.test(key) && (value === 0 || value === 1 || value === '0' || value === '1'))) {
    return Number(value) ? vm.$t('system.on') : vm.$t('system.off');
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?Z?)?$/.test(value)) {
    // A date field comes as midnight UTC; read only the day, so it doesn't move a day back west of UTC
    const dateOnly = /^\d{4}-\d{2}-\d{2}([ T]00:00(:00)?(\.0+)?Z?)?$/.test(value);
    return (dateOnly ? moment(value.slice(0, 10), 'YYYY-MM-DD') : moment(value)).format('D MMM YYYY');
  }
  if (typeof value === 'object') {
    return itemName(value);
  }
  // Codes such as pending, completed, rejected or neutral, as the app labels them
  if (typeof value === 'string' && /^[a-z]+(_[a-z]+)*$/.test(value)) {
    if (vm.$te('activity_log.values.' + value)) {
      return vm.$t('activity_log.values.' + value);
    }
    if (vm.$te('system.' + value)) {
      return vm.$t('system.' + value);
    }
  }
  return plain(value);
}

// Word-level differences between two texts, as parts marked same / del / ins
export function textDiff(before, after) {
  const a = plain(before || '').split(/(\s+)/);
  const b = plain(after || '').split(/(\s+)/);
  // Long texts: the common start and end, and the changed middle as a whole
  if (a.length * b.length > 250000) {
    let start = 0;
    while (start < a.length && start < b.length && a[start] === b[start]) {
      start++;
    }
    let end = 0;
    while (end < a.length - start && end < b.length - start && a[a.length - 1 - end] === b[b.length - 1 - end]) {
      end++;
    }
    return [
      { kind: 'same', text: a.slice(0, start).join('') },
      { kind: 'del', text: a.slice(start, a.length - end).join('') },
      { kind: 'ins', text: b.slice(start, b.length - end).join('') },
      { kind: 'same', text: a.slice(a.length - end).join('') }
    ].filter(part => part.text);
  }
  const table = [];
  for (let i = a.length; i >= 0; i--) {
    table[i] = [];
    for (let j = b.length; j >= 0; j--) {
      table[i][j] = i === a.length || j === b.length ? 0 : (a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]));
    }
  }
  const parts = [];
  const push = (kind, text) => {
    const last = parts[parts.length - 1];
    if (last && last.kind === kind) {
      last.text += text;
    } else {
      parts.push({ kind: kind, text: text });
    }
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('same', a[i]);
      i++;
      j++;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push('del', a[i++]);
    } else {
      push('ins', b[j++]);
    }
  }
  while (i < a.length) {
    push('del', a[i++]);
  }
  while (j < b.length) {
    push('ins', b[j++]);
  }
  return parts;
}

function listKey(item) {
  return item && typeof item === 'object' && item.id !== undefined ? 'id:' + item.id : 'n:' + itemName(item);
}

/**
 * The changed fields of an update: [{ key, label, kind: 'value' | 'text' | 'list' | 'edited', from, to, added, removed, parts }]
 *
 * @param vm component, for translations
 * @param {Object} attributes new values
 * @param {Object} old old values
 * @param {Object} options labels: { key: translation }, formatters: { key: value => text }, hidden: [keys]
 */
export function activityChanges(vm, attributes, old, options = {}) {
  if (!attributes || typeof attributes !== 'object') {
    return [];
  }
  old = old || {};
  const hidden = HIDDEN.concat(options.hidden || []);
  const changes = [];
  Object.keys(attributes).forEach(key => {
    const to = attributes[key];
    const from = old[key];
    if (hidden.indexOf(key) !== -1 || JSON.stringify(to) === JSON.stringify(from === undefined ? null : from)) {
      return;
    }
    if (from === undefined && Object.keys(old).length && isEmpty(to)) {
      return;
    }
    // A reference: shown by the name of the related item logged next to it, if any
    if (/_id$/.test(key)) {
      const base = key.replace(/_id$/, '');
      const camel = base.replace(/_([a-z])/g, (m, c) => c.toUpperCase());
      const toItem = attributes[base] || attributes[camel];
      const fromItem = old[base] || old[camel];
      if (toItem && typeof toItem === 'object' && !Array.isArray(toItem)) {
        changes.push({ key: key, label: label(vm, base, options.labels), kind: 'value', from: fromItem ? itemName(fromItem) : null, to: itemName(toItem) });
      }
      return;
    }
    if (Array.isArray(to) || Array.isArray(from)) {
      const before = Array.isArray(from) ? from : [];
      const after = Array.isArray(to) ? to : [];
      const beforeKeys = before.map(listKey);
      const afterKeys = after.map(listKey);
      const added = after.filter(item => beforeKeys.indexOf(listKey(item)) === -1).map(itemName).filter(Boolean);
      const removed = before.filter(item => afterKeys.indexOf(listKey(item)) === -1).map(itemName).filter(Boolean);
      if (added.length || removed.length) {
        changes.push({ key: key, label: label(vm, key, options.labels), kind: 'list', added: added, removed: removed });
      }
      return;
    }
    // Related items logged whole (e.g. processGroup next to process_group_id) are covered by their reference
    if (to && typeof to === 'object') {
      return;
    }
    if (isJson(to) || isJson(from)) {
      changes.push({ key: key, label: label(vm, key, options.labels), kind: 'edited' });
      return;
    }
    const longText = typeof to === 'string' && (to.length > LONG_TEXT || /\n|<\/?(p|br|li|div)/i.test(to))
      || typeof from === 'string' && (from.length > LONG_TEXT || /\n|<\/?(p|br|li|div)/i.test(from));
    if (longText) {
      changes.push({ key: key, label: label(vm, key, options.labels), kind: 'text', fromText: from, toText: to, parts: textDiff(from, to) });
      return;
    }
    changes.push({
      key: key,
      label: label(vm, key, options.labels),
      kind: 'value',
      from: formatValue(vm, key, from, options.formatters),
      to: formatValue(vm, key, to, options.formatters),
      hadFrom: from !== undefined
    });
  });
  return changes;
}

// The values an item was created with, for "Show all values"
export function activityValues(vm, values, options = {}) {
  if (!values || typeof values !== 'object') {
    return [];
  }
  const hidden = HIDDEN.concat(options.hidden || []).concat(['name', 'title']);
  // Switches that are off say nothing about the new item
  const off = value => value === false || value === 0 || value === '0';
  return Object.keys(values)
    .filter(key => hidden.indexOf(key) === -1 && !/_id$/.test(key) && !isEmpty(values[key]) && !off(values[key]) && typeof values[key] !== 'object' && !isJson(values[key]))
    .map(key => ({ key: key, label: label(vm, key, options.labels), value: formatValue(vm, key, values[key], options.formatters) }))
    .filter(row => row.value !== null && row.value.length <= 160);
}

// Several saves of one item in a row, as one list of changes: the first value before and the last after
export function mergeChanges(lists) {
  const merged = [];
  const byKey = {};
  // lists are newest first
  lists.slice().reverse().forEach(changes => {
    (changes || []).forEach(change => {
      const known = byKey[change.key];
      if (!known) {
        const copy = Object.assign({}, change, { added: (change.added || []).slice(), removed: (change.removed || []).slice() });
        byKey[change.key] = copy;
        merged.push(copy);
        return;
      }
      if (change.kind === 'list') {
        change.added.forEach(name => {
          const removedAt = known.removed.indexOf(name);
          if (removedAt !== -1) {
            known.removed.splice(removedAt, 1);
          } else if (known.added.indexOf(name) === -1) {
            known.added.push(name);
          }
        });
        change.removed.forEach(name => {
          const addedAt = known.added.indexOf(name);
          if (addedAt !== -1) {
            known.added.splice(addedAt, 1);
          } else if (known.removed.indexOf(name) === -1) {
            known.removed.push(name);
          }
        });
      } else if (change.kind === 'text' && known.kind === 'text') {
        known.toText = change.toText;
        known.parts = textDiff(known.fromText, known.toText);
      } else {
        known.to = change.to;
        known.kind = change.kind;
        known.parts = change.parts;
      }
    });
  });
  return merged.filter(change => change.kind !== 'list' || change.added.length || change.removed.length)
    .filter(change => change.kind !== 'value' || change.from !== change.to);
}
